import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  Select,
  Pill,
  Flex,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
  IconRefresh,
  IconChevronRight,
  IconChevronDown,
} from "@tabler/icons-react";
import {
  useRolesAdmin,
  useCreateRole,
  useUpdateRole,
  useToggleRoleActive,
} from "../../../hooks/admin/useRoles";
import {
  useRolePermissionsByRoleId,
  useCreateRolePermission,
  useDeleteRolePermission,
} from "../../../hooks/admin/useRolePermissions";
import { usePermissionsAdmin } from "../../../hooks/admin/usePermissions";
import RoleForm, {
  RoleFormValues,
} from "../../../components/admin/roles/RoleForm";
import { Role } from "../../../services/admin/roles.service";
import { formatPermissionLabel } from "../../../utils/formatters";

const ITEMS_PER_PAGE = 20;

interface RoleFilters {
  status?: "all" | "active" | "inactive";
}

function RolePermissionsRow({ roleId }: { roleId: string }) {
  const { data: rolePermissions = [], isLoading } =
    useRolePermissionsByRoleId(roleId);
  const { data: allPermissions = [] } = usePermissionsAdmin();
  const createRolePermission = useCreateRolePermission();
  const deleteRolePermission = useDeleteRolePermission();
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [selectedPermissionId, setSelectedPermissionId] = useState<
    string | null
  >(null);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] =
    useDisclosure(false);
  const [permToRemove, setPermToRemove] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const assignedPermissionIds = new Set(
    rolePermissions.map((rp) => rp.permissionId)
  );

  const availablePermissions = allPermissions
    .filter((p) => !assignedPermissionIds.has(p.id))
    .map((p) => ({
      value: p.id,
      label: formatPermissionLabel(p),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleAddPermission = async () => {
    if (!selectedPermissionId) return;
    await createRolePermission.mutateAsync({
      roleId,
      permissionId: selectedPermissionId,
    });
    setSelectedPermissionId(null);
    closeAdd();
  };

  const handleRemoveClick = (
    rpId: string,
    permName: string,
    permDescription: string | null | undefined
  ) => {
    setPermToRemove({ id: rpId, name: permDescription || permName });
    openConfirm();
  };

  const confirmRemove = async () => {
    if (permToRemove) {
      await deleteRolePermission.mutateAsync(permToRemove.id);
      closeConfirm();
      setPermToRemove(null);
    }
  };

  if (isLoading) {
    return (
      <Center py="sm">
        <Loader size="sm" />
      </Center>
    );
  }

  const sorted = [...rolePermissions].sort((a, b) =>
    (a.permission?.name || "").localeCompare(b.permission?.name || "")
  );

  return (
    <Box px="md" pb="md">
      <Group justify="space-between" mb="xs">
        <Text size="sm" fw={600} c="dimmed">
          Permissions ({rolePermissions.length})
        </Text>
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconPlus size={12} />}
          onClick={openAdd}
        >
          Add
        </Button>
      </Group>

      {sorted.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No permissions assigned to this role.
        </Text>
      ) : (
        <Flex wrap="wrap" gap={6}>
          {sorted.map((rp) => (
            <Badge
              key={rp.id}
              variant="light"
              color="blue"
              size="md"
              rightSection={
                <ActionIcon
                  size={14}
                  variant="transparent"
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveClick(
                      rp.id,
                      rp.permission?.name || "",
                      rp.permission?.description
                    );
                  }}
                >
                  <IconX size={10} />
                </ActionIcon>
              }
            >
              {formatPermissionLabel(rp.permission)}
            </Badge>
          ))}
        </Flex>
      )}

      <Modal
        opened={addOpened}
        onClose={closeAdd}
        title="Add Permission"
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Permission"
            placeholder="Select a permission to add"
            data={availablePermissions}
            value={selectedPermissionId}
            onChange={setSelectedPermissionId}
            searchable
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              onClick={handleAddPermission}
              loading={createRolePermission.isPending}
              disabled={!selectedPermissionId}
            >
              Add Permission
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmOpened}
        onClose={closeConfirm}
        title="Remove Permission"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text>
            Remove <strong>{permToRemove?.name}</strong> from this role?
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmRemove}
              loading={deleteRolePermission.isPending}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}

export default function RolesAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleToToggle, setRoleToToggle] = useState<Role | null>(null);
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<RoleFilters>({});
  const [tempFilters, setTempFilters] = useState<RoleFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const { data: roles = [], isLoading, refetch } = useRolesAdmin();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const toggleRoleActive = useToggleRoleActive();

  // Filter roles
  const filteredRoles = useMemo(() => {
    let result = roles;

    // Filter by status
    if (filters.status === "active") {
      result = result.filter((r) => r.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((r) => !r.isActive);
    }

    // Filter by search text
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (role) =>
          role.name.toLowerCase().includes(searchLower) ||
          role.description?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [roles, debouncedSearch, filters]);

  // Check if any filters are active
  const hasActiveFilters = filters.status && filters.status !== "all";

  // Pagination
  const totalPages = Math.ceil(filteredRoles.length / ITEMS_PER_PAGE);
  const paginatedRoles = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredRoles.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredRoles, page]);

  // Handlers
  const handleAdd = () => {
    setEditingRole(null);
    openFormModal();
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    openFormModal();
  };

  const handleSubmit = async (values: RoleFormValues) => {
    if (editingRole) {
      await updateRole.mutateAsync({
        id: editingRole.id,
        ...values,
      });
    } else {
      await createRole.mutateAsync(values);
    }
    closeFormModal();
  };

  const handleToggleActive = (role: Role) => {
    setRoleToToggle(role);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (roleToToggle) {
      await toggleRoleActive.mutateAsync({
        id: roleToToggle.id,
        isActive: !roleToToggle.isActive,
      });
      closeConfirmModal();
      setRoleToToggle(null);
    }
  };

  const handleOpenFilterModal = () => {
    setTempFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleRemoveFilter = (key: keyof RoleFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setPage(1);
  };

  // Get display name for status filter
  const getFilterDisplayName = (): string | null => {
    if (filters.status === "active") return "Active Only";
    if (filters.status === "inactive") return "Inactive Only";
    return null;
  };

  const toggleExpand = (roleId: string) => {
    setExpandedRoleId((prev) => (prev === roleId ? null : roleId));
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Roles</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage user roles and access levels. Expand a row to view and
                manage its permissions.
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" align="center">
                    <Text size="sm" c="dimmed">
                      Filters:
                    </Text>
                    <Pill
                      withRemoveButton
                      onRemove={() => handleRemoveFilter("status")}
                    >
                      {getFilterDisplayName()}
                    </Pill>
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  Showing {filteredRoles.length} of {roles.length} roles
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Roles ({filteredRoles.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredRoles.length)} of{" "}
                      {filteredRoles.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredRoles.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search || hasActiveFilters
                    ? "No roles match the current filters"
                    : "No roles found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }} />
                      <Table.Th>Role Name</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedRoles.map((role) => {
                      const isExpanded = expandedRoleId === role.id;
                      return (
                        <>
                          <Table.Tr
                            key={role.id}
                            style={{
                              opacity: role.isActive ? 1 : 0.5,
                              cursor: "pointer",
                            }}
                            onClick={() => toggleExpand(role.id)}
                          >
                            <Table.Td>
                              {isExpanded ? (
                                <IconChevronDown size={16} />
                              ) : (
                                <IconChevronRight size={16} />
                              )}
                            </Table.Td>

                            {/* Name */}
                            <Table.Td>
                              <Text size="sm" fw={500}>
                                {role.name}
                              </Text>
                            </Table.Td>

                            {/* Description */}
                            <Table.Td>
                              <Text size="sm" lineClamp={2}>
                                {role.description || "—"}
                              </Text>
                            </Table.Td>

                            {/* Created */}
                            <Table.Td>
                              <Text size="sm">
                                {new Date(role.createdAt).toLocaleDateString()}
                              </Text>
                            </Table.Td>

                            {/* Status */}
                            <Table.Td>
                              <Badge
                                color={role.isActive ? "green" : "red"}
                                variant="light"
                                size="sm"
                              >
                                {role.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </Table.Td>

                            {/* Actions */}
                            <Table.Td>
                              <Group gap="xs">
                                <Tooltip label="Edit">
                                  <ActionIcon
                                    variant="subtle"
                                    color="blue"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(role);
                                    }}
                                  >
                                    <IconEdit size={18} />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip
                                  label={
                                    role.isActive ? "Deactivate" : "Activate"
                                  }
                                >
                                  <ActionIcon
                                    variant="subtle"
                                    color={role.isActive ? "red" : "green"}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleActive(role);
                                    }}
                                  >
                                    {role.isActive ? (
                                      <IconX size={18} />
                                    ) : (
                                      <IconCheck size={18} />
                                    )}
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                          {isExpanded && (
                            <Table.Tr key={`${role.id}-perms`}>
                              <Table.Td colSpan={6}>
                                <RolePermissionsRow roleId={role.id} />
                              </Table.Td>
                            </Table.Tr>
                          )}
                        </>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Role Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editingRole ? "Edit Role" : "Add New Role"}
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <RoleForm
              initialValues={editingRole || undefined}
              onSubmit={handleSubmit}
              isLoading={createRole.isPending || updateRole.isPending}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createRole.isPending || updateRole.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editingRole ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Roles"
          size="sm"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active Only" },
                { value: "inactive", label: "Inactive Only" },
              ]}
              value={tempFilters.status || "all"}
              onChange={(value) =>
                setTempFilters((prev) => ({
                  ...prev,
                  status: value as RoleFilters["status"],
                }))
              }
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Activate/Deactivate */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={roleToToggle?.isActive ? "Deactivate Role" : "Activate Role"}
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {roleToToggle?.isActive ? "deactivate" : "activate"} the role{" "}
              <strong>{roleToToggle?.name}</strong>?
            </Text>
            {roleToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated roles will no longer be available for assignment to
                users.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={roleToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleRoleActive.isPending}
              >
                {roleToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
